import React from 'react';
import { useSelector } from 'react-redux';
import block from 'bem-cn';

import Button from 'components/Button';
import SVG from 'components/SVG';
import InputDateCustom from 'components/InputDateCustom';
import Select from 'components/Selectors/Select';

import compareSVG from 'features/generalReport/img/compare.svg';
import arrowSVG from 'features/generalReport/img/arrow.svg';
import upSVG from 'features/generalReport/img/up.svg';
import downSVG from 'features/generalReport/img/down.svg';
import infoSVG from 'features/generalReport/img/info.svg';

import formatNumber from 'shared/helpers/formatNumber';

import './Table.scss';

const b = block('reports-table-general-mobile');

const Table = ({ values, handlers }) => {
  const locale = useSelector(state => state.locale.locale);
  const { currency } = useSelector(state => state.auth);

  const makeItem = ({ title, prevValue, diffPercent, currentValue, needCurrency = true, info }) => {
    const [icon, color] = (() => {
      if (diffPercent < 0) return [downSVG, 'red'];
      if (diffPercent > 0) return [upSVG, 'green'];
      return [null, ''];
    })();

    return (
      currentValue !== '-' && (
        <div className={b('table-tr')}>
          <div className={b('table-th')}>{title}</div>
          <div className={b('table-td')}>
            <div className={b('table-td-amount')}>{currentValue}</div>
            {needCurrency && <div className={b('table-td-currency')}>{currency}</div>}
          </div>
          {values.state.withDiff && prevValue !== '-' && (
            <div className={b('table-th-diff')}>
              {icon && <SVG className={b('table-th-diff-icon')} svgProps={{ svg: icon }} />}
              <p className={b('table-th-diff-percent', { color })}>{`${diffPercent}%`}</p>
              <p className={b('table-th-diff-value')}>{prevValue}</p>
            </div>
          )}
          {info && (
            <div className={b('info')}>
              <span className={b('info-text')}>Info</span>
              <SVG className={b('info-icon')} svgProps={{ svg: infoSVG }} />
              <div className={b('info-description')}>{info.description}</div>
            </div>
          )}
        </div>
      )
    );
  };

  return (
    <div className={b()}>
      <div className={b('header')}>
        <div className={b('button')}>
          <Button
            onClick={() => handlers.handleToggleWithDiff(true)}
            text={
              <div className={b('button-content')}>
                <SVG className={b('button-icon', { type: 'small' })} svgProps={{ svg: compareSVG }} />
                {locale.compare}
              </div>
            }
          />
        </div>
        {values.state.withDiff && (
          <div className={b('button', { type: 'standard' })}>
            <Button
              onClick={() => handlers.handleToggleWithDiff(false)}
              color="bordered"
              text={
                <div className={b('button-content')}>
                  <SVG className={b('button-icon')} svgProps={{ svg: arrowSVG }} />
                  {locale.standardValues}
                </div>
              }
            />
          </div>
        )}
      </div>
      <div className={b('table')}>
        {values.state.withDiff && (
          <>
            <div className={b('select')}>
              <Select
                items={values.state.periodItems}
                onChange={v => handlers.handleValueChange('periodItems', v)}
                isCapitalized={false}
              />
            </div>

            <div className={b('diff')}>
              <div className={b('date-input')}>
                <InputDateCustom
                  value={values.state.beginDatePrev}
                  onChange={v => handlers.handleDateChange('beginDatePrev', v)}
                  title={locale.from}
                  fontSize={12}
                  deleteWord=""
                />
              </div>
              <div className={b('date-input')}>
                <InputDateCustom
                  value={values.state.endDatePrev}
                  onChange={v => handlers.handleDateChange('endDatePrev', v)}
                  title={locale.to}
                  fontSize={12}
                  deleteWord=""
                />
              </div>
            </div>
          </>
        )}
        {makeItem({
          currentValue: formatNumber(values.statisticProvider.details.betCount.current, 0),
          diffPercent: values.statisticProvider.details.betCount.percent,
          prevValue: formatNumber(values.statisticProvider.details.betCount.previous, 0),
          title: locale.totalBetCount,
          needCurrency: false,
        })}
        {makeItem({
          currentValue: formatNumber(values.statisticProvider.details.betAmount.current),
          diffPercent: values.statisticProvider.details.betAmount.percent,
          prevValue: formatNumber(values.statisticProvider.details.betAmount.previous),
          title: locale.totalBet,
        })}
        {makeItem({
          currentValue: formatNumber(values.statisticProvider.details.winAmount.current),
          diffPercent: values.statisticProvider.details.winAmount.percent,
          prevValue: formatNumber(values.statisticProvider.details.winAmount.previous),
          title: locale.totalWon,
        })}
        {makeItem({
          currentValue: formatNumber(values.statisticProvider.details.incomeAmount.current),
          diffPercent: values.statisticProvider.details.incomeAmount.percent,
          prevValue: formatNumber(values.statisticProvider.details.incomeAmount.previous),
          title: locale.GGR,
        })}
        {makeItem({
          currentValue:
            values.statisticProvider.details.margin.current !== undefined
              ? `${values.statisticProvider.details.margin.current}%`
              : '-',
          diffPercent: values.statisticProvider.details.margin.percent,
          prevValue:
            values.statisticProvider.details.margin.previous !== undefined
              ? `${values.statisticProvider.details.margin.previous}%`
              : '-',
          title: locale.margin,
          needCurrency: false,
          info: {
            text: locale.margin,
            description: `${locale.margin} = ${locale.GGR} / ${locale.totalBets} × 100%`,
          },
        })}
      </div>
    </div>
  );
};

Table.propTypes = {};

export default Table;
